import { createMemo } from 'solid-js';
import { distributions } from '~data';
import { transactions, type Transaction } from '~/stores/transactions-store';
import { unitsBefore } from '~/stores/positions';

// What each account has actually been paid.
//
// Its own module, small as it is, because of who needs to import it.
// dividend-store computes the same thing in more detail, but it needs
// portfolio-store to scope payments to the active book — so portfolio-store
// cannot import it back without closing a cycle. This depends on the blotter and
// the fund data only, which is all the number needs.
//
// The entitlement rule is not repeated here: `unitsBefore` is the one copy of
// it, shared with dividend-store.

/** Total received, per account id. */
export const receivedByAccount = createMemo(() => {
    const byPosition = new Map<string, Transaction[]>();
    for (const t of transactions) {
        const key = `${t.accountId} ${t.symbol}`;
        const list = byPosition.get(key) ?? [];
        list.push(t);
        byPosition.set(key, list);
    }

    const totals = new Map<string, number>();
    for (const [key, list] of byPosition) {
        const [accountId, symbol] = key.split(' ');
        let sum = 0;
        for (const d of distributions(symbol)) {
            const units = unitsBefore(list, d.exDate);
            if (units > 0) sum += units * d.amount;
        }
        if (sum > 0) totals.set(accountId, (totals.get(accountId) ?? 0) + sum);
    }
    return totals;
});

/** Distributions this account has been paid, in dollars. */
export const receivedIn = (accountId: string): number => receivedByAccount().get(accountId) ?? 0;
